import { createApi } from '@reduxjs/toolkit/query/react'
import { type BaseRequest, type BaseResponse } from '../models'
import { type BillingCustomer, type BundledCreditProduct, type CreditConfig, type CustomerDeltaTrial, type Plan, type PricePeriod } from '../../models/Plan'
import { parseCoinAmount, type WalletType } from '../../models/Wallet'
import { baseQueryWithTokenRefresh } from '../../common/baseQuery'

const _BASE_URL = import.meta.env.REACT_APP_ACCOUNTS_API_BASE_URL!

const normalizeDefaultAmounts = (defaultAmounts: BundledCreditProduct['defaultAmounts']): BundledCreditProduct['defaultAmounts'] => {
  const normalized: BundledCreditProduct['defaultAmounts'] = {}
  for (const [period, amount] of Object.entries(defaultAmounts) as [PricePeriod, number | string][]) {
    normalized[period] = parseCoinAmount(amount)
  }
  return normalized
}

const normalizeCreditConfig = (config: CreditConfig): CreditConfig =>
  config.bundled ? { ...config, bundled: { ...config.bundled, defaultAmounts: normalizeDefaultAmounts(config.bundled.defaultAmounts) } } : config

const normalizePlanCreditAmounts = (plan: Plan): Plan => {
  const creditConfigs: Plan['creditConfigs'] = {}
  for (const [walletType, config] of Object.entries(plan.creditConfigs ?? {}) as [WalletType, CreditConfig][]) {
    creditConfigs[walletType] = normalizeCreditConfig(config)
  }
  return { ...plan, creditConfigs }
}

export enum SubscriptionChangeStatus {
  ACCEPTED = 'accepted',
  CHECKOUT_REQUIRED = 'checkoutRequired',
}

interface SubscriptionChangePayload {
  planId?: string
  period?: PricePeriod
  numberOfSeats?: number
  freeTrial?: boolean
  additionalProductChanges?: { [key: string]: { quantity?: number } }
  paymentType?: string
}

interface SubscriptionChangeRequest extends BaseRequest {
  payload: SubscriptionChangePayload
}

interface DeltaTrialRequest extends BaseRequest {
  planId: string
}

interface SubscriptionChangeResponse {
  result: SubscriptionChangeStatus
  checkoutSessionUrl: string
}

interface PurchasePaidCreditsRequest extends BaseRequest {
  quantity: number
  walletType: WalletType
}

interface CreateInvoicePreviewPayload {
  planId?: string
  period?: PricePeriod
  numberOfSeats?: number
  additionalProductChanges?: { [key: string]: { quantity?: number } }
}

interface CreateInvoicePreviewRequest extends BaseRequest {
  payload?: CreateInvoicePreviewPayload
}

interface CreateInvoicePreviewLines {
  amount: number
  currency: string
  description: string
}
interface CreateInvoicePreviewResponse {
  amountDue: number
  startingBalance: number
  endingBalance: number
  currency: string
  lines: CreateInvoicePreviewLines[]
  periodEnd: string
  periodStart: string
}

interface CreateCustomerPortalSessionResponse {
  url: string
}

interface CustomerSeat {
  available: number
  occupied: number
}

export interface CustomerSeats {
  general: CustomerSeat
  sidecar: CustomerSeat
}

export interface CustomerFreeSeatsResponse {
  seats: CustomerSeats
}

interface SessionAllocationAvailableResponse {
  allowed: boolean
  reason: string
  maxSessionsInPeriod?: number
  usedSessionsInPeriod?: number
}

export const planApi = createApi({
  reducerPath: 'planApi',
  baseQuery: baseQueryWithTokenRefresh(_BASE_URL),
  tagTypes: ['BillingCustomer'],
  endpoints: (builder) => ({
    getPlans: builder.query<BaseResponse<Plan[]>, void>({
      query: () => `/billing/plans`,
      transformResponse: (response: BaseResponse<Plan[]>): BaseResponse<Plan[]> => ({
        ...response,
        data: response.data.map(normalizePlanCreditAmounts),
      }),
    }),
    getBillingCustomer: builder.query<BaseResponse<BillingCustomer>, BaseRequest>({
      query: (request) => `/billing/customers/${request.accountId}`,
      // providesTags: ['BillingCustomer'],
    }),
    getBillingCustomerFreeSeats: builder.query<BaseResponse<CustomerFreeSeatsResponse>, BaseRequest>({
      query: (request) => `/billing/customers/${request.accountId}/free-seats`,
      providesTags: ['BillingCustomer'],
    }),
    getBillingCustomerSessionAllocationAvailable: builder.query<BaseResponse<SessionAllocationAvailableResponse>, BaseRequest>({
      query: (request) => ({ url: `/billing/customers/${request.accountId}/session-allocation-available`, method: 'GET' }),
      providesTags: ['BillingCustomer'],
    }),
    changePlan: builder.mutation<BaseResponse<SubscriptionChangeResponse>, SubscriptionChangeRequest>({
      query: (request) => ({
        url: `/billing/subscriptions/${request.accountId}/change`,
        method: 'POST',
        body: request.payload,
      }),
      invalidatesTags: ['BillingCustomer'],
    }),
    startDeltaTrial: builder.mutation<null, DeltaTrialRequest>({
      query: (request) => ({
        url: `/billing/subscriptions/${request.accountId}/start-delta-trial`,
        method: 'POST',
        body: { planId: request.planId },
      }),
    }),
    cancelDeltaTrial: builder.mutation<null, DeltaTrialRequest>({
      query: (request) => ({
        url: `/billing/subscriptions/${request.accountId}/cancel-delta-trial`,
        method: 'POST',
        body: { planId: request.planId },
      }),
    }),
    getDeltaTrials: builder.query<CustomerDeltaTrial[], BaseRequest>({
      query: (request) => `/billing/subscriptions/${request.accountId}/delta-trials`,
      transformResponse(response: BaseResponse<CustomerDeltaTrial[]>) {
        return response.data
      },
    }),
    purchasePaidCredits: builder.mutation<void, PurchasePaidCreditsRequest>({
      query: (request) => ({
        url: `/billing/subscriptions/${request.accountId}/purchase-paid-credits`,
        method: 'POST',
        body: { quantity: request.quantity, walletType: request.walletType },
      }),
    }),
    createCustomerPortalSession: builder.mutation<BaseResponse<CreateCustomerPortalSessionResponse>, BaseRequest>({
      query: (request) => ({
        url: `/billing/customers/${request.accountId}/create-customer-portal-session`,
        method: 'POST',
        redirect: 'follow',
      }),
    }),
    createInvoicePreview: builder.mutation<BaseResponse<CreateInvoicePreviewResponse>, CreateInvoicePreviewRequest>({
      query: (request) => ({
        url: `/billing/customers/${request.accountId}/create-invoice-preview`,
        method: 'POST',
        body: { ...(request.payload && { forSubscriptionChange: request.payload }) },
      }),
    }),
  }),
})

export const {
  useGetPlansQuery,
  useGetBillingCustomerQuery,
  useChangePlanMutation,
  useCreateCustomerPortalSessionMutation,
  useCreateInvoicePreviewMutation,
  useGetBillingCustomerFreeSeatsQuery,
  useGetBillingCustomerSessionAllocationAvailableQuery,
  usePurchasePaidCreditsMutation,
  useStartDeltaTrialMutation,
  useCancelDeltaTrialMutation,
  useGetDeltaTrialsQuery,
} = planApi
